/* ============================================================================
   Slider.tsx — range input minimal, style 3secgraphic.
   Custom thumb 8x8 ink square, track 1px line.
   ============================================================================ */

import type { ChangeEvent } from 'react';

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  /** Optionnel — fonction de format pour l'affichage de la valeur. Default: 2 décimales si step < 1, sinon entier. */
  format?: (value: number) => string;
  /** Optionnel — id pour le label htmlFor */
  id?: string;
  /** Optionnel — désactive le slider */
  disabled?: boolean;
}

function defaultFormat(value: number, step: number): string {
  if (step >= 1) return Math.round(value).toString();
  // Nombre de décimales du step
  const decimals = (step.toString().split('.')[1] || '').length;
  return value.toFixed(Math.min(decimals, 3));
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
  id,
  disabled = false,
}: SliderProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };

  const display = format ? format(value) : defaultFormat(value, step);
  const inputId = id ?? `slider-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="controls-slider">
      <div className="controls-row">
        <label className="controls-label" htmlFor={inputId}>
          {label}
        </label>
        <span className="controls-value" aria-live="polite">
          {display}
        </span>
      </div>
      <input
        id={inputId}
        type="range"
        className="controls-range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        aria-label={label}
      />
    </div>
  );
}
